import React from "react";

import {
	Typography,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableContainer,
	Paper,
	Pagination,
	Box,
	CircularProgress
} from "@mui/material";
// import { Filt } from "@mui/icons-material";
import TableCell from "@mui/material/TableCell";
import { v4 as uuidv4 } from "uuid";
import { TableHeader, StyledTableCell } from "./styles";
import NoData from "../../assets/imgs/search.svg";

export interface IColumn<TData> {
	label?: JSX.Element | string;
	dataKey?: string;
	Cell?: (c: { rowData: TData; rowIndex: number }) => JSX.Element;
}

export interface ITableProps<T> {
	tableLabel: string;
	columns: IColumn<T>[];
	data: Array<T>;
	page?: number;
	rowsPerPage?: number;
	totalItems?: number;
	onPageChange?: (page: number) => void;
	filters?: React.ReactNode;
	isLoading?: boolean;
	onRowClick?: (rowData: T) => void;
}

const MaterialTable = <T extends {}>({
	tableLabel,
	columns,
	data,
	page,
	rowsPerPage,
	totalItems,
	onPageChange,
	filters,
	isLoading,
	onRowClick
}: ITableProps<T>) => {
	const handleChangePage = (event: unknown, newPage: number) => {
		onPageChange && onPageChange(newPage);
	};

	return (
		<TableContainer component={Paper}>
			<TableHeader p={2}>
				<Box>
					<Typography variant={"h6"} fontWeight={"bold"}>
						{tableLabel}
					</Typography>
				</Box>
				<Box>{filters}</Box>
			</TableHeader>
			<Table sx={{ minWidth: 650 }} aria-label="simple table">
				<TableHead>
					<TableRow>
						{columns.map(({ label, dataKey }) => (
							<StyledTableCell key={dataKey}>{label}</StyledTableCell>
						))}
					</TableRow>
				</TableHead>
				{isLoading ? (
					<TableBody>
						<TableRow>
							<TableCell scope="row" colSpan={columns.length}>
								<Box width={"100%"} height={"50vh"} display={"flex"} justifyContent={"center"} alignItems={"center"}>
									<CircularProgress />
								</Box>
							</TableCell>
						</TableRow>
					</TableBody>
				) : (
					<TableBody>
						{data.length > 0 ? (
							data.map((item, index) => (
								<TableRow
									key={uuidv4()}
									hover={!!onRowClick}
									onClick={onRowClick ? () => onRowClick(item) : undefined}
									sx={{
										"&:last-child td, &:last-child th": { border: 0 },
										...(onRowClick && { cursor: "pointer" })
									}}
								>
									{columns.map(({ Cell }) => (
										<TableCell key={uuidv4()} component="th" scope="row">
											{Cell && Cell({ rowData: item, rowIndex: index })}
										</TableCell>
									))}
								</TableRow>
							))
						) : (
							<TableRow>
								<TableCell scope="row" colSpan={columns.length}>
									<Box m={4} display={"flex"} justifyContent={"center"} alignItems={"center"} flexDirection={"column"}>
										<img src={NoData} alt="Invitation not found" width={250} />
										<Box mt={4}>
											<Typography variant={"h6"} fontWeight={"bold"}>
												No result found
											</Typography>
										</Box>
									</Box>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				)}
			</Table>

			{!!(page && rowsPerPage && totalItems) && (
				<Box p={2} display={"flex"} justifyContent={"flex-end"}>
					<Pagination
						page={page}
						count={Math.ceil(totalItems / rowsPerPage)}
						shape="rounded"
						onChange={handleChangePage}
					/>
				</Box>
			)}
		</TableContainer>
	);
};

export default MaterialTable;
